import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { StratQuestionType } from '../../../../types/welcome';
import { RadioQuestionType, QuestionTypeEnum } from '../../../../types/Question';
import { AnswerValueType } from '../../../../types/Answer';
import { FormGroup } from '../../../atoms/LoginTextField';
import SelectField from '../../../atoms/SelectField';
import Question from '../../Question/Question';
import useVocalisation from '../../../../hooks/useVocalisation';

/**
 * Stratification questions with at most this many choices are rendered with the
 * same <Question> card used in questionnaires (radio buttons). Questions with
 * more choices keep the dropdown (liste déroulante). Tweak this to change the
 * cutoff.
 */
const MAX_CHOICES_AS_RADIO = 5;

interface DisplayStratQuestionsProps {
  stratQuestions: StratQuestionType[];
  values: {[key: string]: string};
  setFieldValue(key: string, value: string): void;
  errors: any;
  touched: any;
};

// Adapt a stratification question to the questionnaire RADIOBUTTON shape, so it
// can be rendered by the shared <Question> component. The card, vocalisation
// and speech-recognition behaviour then come from the exact same component as
// real questionnaire questions — improve <Question> and this improves too.
const toRadioQuestion = (question: StratQuestionType): RadioQuestionType => ({
  id: question.id,
  title: question.label,
  description: '',
  export_code: question.export_code,
  integral: { is_integral: false, integral_errorcode: null },
  is_mandatory_question: true,
  is_active: true,
  score_coefficient: 0,
  order: question.order,
  version: String(question.version),
  type: QuestionTypeEnum.RADIOBUTTON,
  detail: {
    id: question.id,
    version: question.version,
    answers: question.answers.map(({ id, label, order }) => ({ id, label, order })),
  },
});

const DisplayStratQuestions = ({ stratQuestions, values, setFieldValue, touched, errors}: DisplayStratQuestionsProps): ReactElement => {
  const { t } = useTranslation();
  const { readText } = useVocalisation();

  const isRadio = (question: StratQuestionType): boolean =>
    question.answers.length <= MAX_CHOICES_AS_RADIO;

  // Few choices -> reuse the questionnaire <Question> card.
  const renderRadio = (question: StratQuestionType): ReactElement => {
    const { id: questionId } = question;
    const selected = values[questionId];
    const hasError = touched[questionId] && errors[questionId];

    return (
      <Question
        key={questionId}
        question={toRadioQuestion(question)}
        answer={selected ? Number(selected) : null}
        onAnswerChange={(value: AnswerValueType): void =>
          setFieldValue(
            questionId.toString(),
            typeof value === 'number' && !Number.isNaN(value) ? String(value) : ''
          )
        }
        ruleMessage={hasError ? t('form_error_required') : undefined}
        readText={readText}
        // Tighter than the Card default (30px): stacked question cards read as
        // one form, not distant islands.
        cardMargin="12px 20px"
      />
    );
  };

  // Many choices -> keep the existing dropdown, unchanged.
  const renderDropdown = (question: StratQuestionType): ReactElement => {
    const { id: questionId, label, answers } = question;
    const options = answers.map(({id, label})=>({ text: label, id: id.toString() }))

    return (
      <SelectField
        key={questionId}
        value={values[questionId]}
        placeholder={t('home_stratification_placeholder')}
        label={label}
        options={options}
        error={touched[questionId] && errors[questionId]}
        onChange={value => setFieldValue(questionId.toString(), value)}
      />
    );
  };

  // Keep questions in their defined order: radio questions render as standalone
  // cards, while consecutive dropdown questions are grouped in a single
  // FormGroup so they keep their original stacked appearance.
  const blocks: ReactElement[] = [];
  let dropdownRun: StratQuestionType[] = [];

  const flushDropdownRun = (): void => {
    if (dropdownRun.length) {
      blocks.push(
        <FormGroup key={`dropdowns-${dropdownRun[0].id}`}>
          {dropdownRun.map(renderDropdown)}
        </FormGroup>
      );
      dropdownRun = [];
    }
  };

  stratQuestions.forEach((question) => {
    if (isRadio(question)) {
      flushDropdownRun();
      blocks.push(renderRadio(question));
    } else {
      dropdownRun.push(question);
    }
  });
  flushDropdownRun();

  return <>{blocks}</>;
};

export default DisplayStratQuestions;
